import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, faEye, faHistory, faUndo, faRedo, faPlus, faArrowLeft,
  faTrash, faCopy, faArrowUp, faArrowDown, faChevronDown, 
  faChevronRight, faTimes, faCheck, faX, faInfoCircle, faCog,
  faUpload, faDownload, faBox, faGripVertical, faAngleDoubleDown, faAngleDoubleUp
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionnaireTemplate, Question } from '../../types/questionnaire';
import { 
  getQuestionnaireTemplate, 
  createQuestionnaireTemplate, 
  updateQuestionnaireTemplate,
  uploadAdminFiles 
} from '../../services/api';
import PreviewModal from './PreviewModal';
import VersionHistory from './VersionHistory';
import ArticleSelector from './ArticleSelector';
import Loader from '../common/Loader';
import RichTextEditor from '../common/RichTextEditor';
import { getAllArticleIds } from '../../services/articleApi';

const QuestionnaireEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(id);
  const successMessage = (location.state as any)?.message;

  const [template, setTemplate] = useState<QuestionnaireTemplate>({
    type: 'producer',
    title: { de: '', en: '' },
    description: { de: '', en: '' },
    version: '1.0',
    isActive: true,
    questions: [],
    sections: [],
    requiresArticles: false,
    selectedArticles: []
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [undoStack, setUndoStack] = useState<QuestionnaireTemplate[]>([]);
  const [redoStack, setRedoStack] = useState<QuestionnaireTemplate[]>([]);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showArticleSelector, setShowArticleSelector] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      loadTemplate(id);
    }
  }, [id, isEditing]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        window.history.replaceState({}, document.title);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const response = await getQuestionnaireTemplate(templateId);
      setTemplate(response.data);
      setUndoStack([response.data]);
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveToUndoStack = () => {
    setUndoStack(prev => [...prev, template]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length > 1) {
      const newUndoStack = [...undoStack];
      const currentState = newUndoStack.pop()!;
      const previousState = newUndoStack[newUndoStack.length - 1];
      
      setRedoStack(prev => [...prev, currentState]);
      setUndoStack(newUndoStack);
      setTemplate(previousState);
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const newRedoStack = [...redoStack];
      const nextState = newRedoStack.pop()!;
      
      setUndoStack(prev => [...prev, nextState]);
      setRedoStack(newRedoStack);
      setTemplate(nextState);
    }
  };

  const validateTemplate = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!template.title.de?.trim() && !template.title.en?.trim()) {
      newErrors.titleDe = 'Titel ist erforderlich (mindestens eine Sprache)';
    }
    if (!template.description.de?.trim() && !template.description.en?.trim()) {
      newErrors.descriptionDe = 'Beschreibung ist erforderlich (mindestens eine Sprache)';
    }
    if (template.questions.length === 0) {
      newErrors.questions = 'Mindestens eine Frage ist erforderlich';
    }

    // Erst alle Signatur-Fragen reparieren, dann validieren
    const updatedQuestions = template.questions.map(question => {
      if (question.type === 'signature' && (!question.signatureText || !question.signatureText.trim())) {
        return {
          ...question,
          signatureText: 'Ich bestätige hiermit...'
        };
      }
      return question;
    });

    // Template aktualisieren falls nötig
    if (JSON.stringify(updatedQuestions) !== JSON.stringify(template.questions)) {
      setTemplate(prev => ({
        ...prev,
        questions: updatedQuestions
      }));
    }

    // Validierung für Fragen-Label
    template.questions.forEach((question, index) => {
      if (!question.label.de.trim()) {
        newErrors[`question_${index}_label`] = `Frage-Text für Frage ${index + 1} ist erforderlich`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateTemplate()) {
      return;
    }

    try {
      setSaving(true);

      // Prüfe ob (nahezu) alle verfügbaren Artikel ausgewählt sind.
      // Gesamtanzahl live von der API holen statt fest verdrahteter Zahl.
      let isAllArticlesSelected = false;
      if (template.requiresArticles) {
        const selectedCount = template.selectedArticles?.length || 0;
        try {
          const { totalCount } = await getAllArticleIds({});
          // Toleranz von 100 Artikeln (z. B. zwischenzeitlich neu angelegte)
          isAllArticlesSelected = totalCount > 0 && selectedCount >= totalCount - 100;
        } catch (err) {
          console.error('Konnte Artikel-Gesamtanzahl nicht laden:', err);
        }
      }

      // Sanitize template data before saving
      const sanitizedTemplate = {
        ...template,
        // Wenn alle Artikel ausgewählt → nur Flag setzen, keine IDs
        allArticlesSelected: template.requiresArticles && isAllArticlesSelected ? true : false,
        selectedArticles: template.requiresArticles && !isAllArticlesSelected 
          ? (template.selectedArticles || []) 
          : undefined, // Keine IDs wenn alle ausgewählt
        // Ensure questions have proper structure
        questions: template.questions.map(q => ({
          ...q,
          // Remove articleIds from options if requiresArticleSelection is false
          options: q.options?.map(opt => ({
            ...opt,
            articleIds: q.requiresArticleSelection ? opt.articleIds : undefined
          }))
        })),
        // WICHTIG: Entferne articles array - nur selectedArticles (IDs) senden
        articles: undefined
      };

      if (isEditing && id) {
        const response = await updateQuestionnaireTemplate(id, sanitizedTemplate);
        const newVersionId = response.data._id; // Neue Version ID

        // Navigate to the NEW version's edit page so user sees the version changed
        navigate(`/questionnaires/edit/${newVersionId}`, { 
          state: { 
            message: `Neue Version ${response.data.version} erfolgreich erstellt!`
          }
        });
      } else {
        const response = await createQuestionnaireTemplate(sanitizedTemplate);

        // For new templates, go back to list
        navigate('/questionnaires', { 
          state: { 
            filterType: template.type,
            highlightId: response.data._id
          }
        });
      }
    } catch (error: any) {
      console.error('Error saving template:', error);

      // Show error message to user
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Fehler beim Speichern des Fragebogens';
      const details = error?.response?.data?.details ? '\n\nDetails: ' + JSON.stringify(error.response.data.details, null, 2) : '';
      alert(`Fehler beim Speichern:\n${errorMessage}${details}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateChange = (field: string, value: any) => {
    saveToUndoStack();
    setTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTitleChange = (value: string) => {
    saveToUndoStack();
    setTemplate(prev => ({
      ...prev,
      title: { ...prev.title, de: value, en: value }
    }));
  };

  const handleDescriptionChange = (value: string) => {
    saveToUndoStack();
    setTemplate(prev => ({
      ...prev,
      description: { ...prev.description, de: value, en: value }
    }));
  };

  const buildNewQuestion = (type: Question['type'] = 'text', order = 0): Question => ({
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type,
    label: { de: '', en: '' },
    required: false,
    order,
    ...(type === 'signature' && { signatureText: 'Ich bestätige hiermit...' }),
    ...(type === 'info_text' && {
      infoText: { de: '', en: '' },
      required: false // Info-Texte sind nie Pflicht
    }),
    ...(type === 'admin_file' && {
      adminFiles: [],
      required: false // Admin-Dateien sind nie Pflicht
    })
  });

  const handleAddQuestion = (type: Question['type'] = 'text') => {
    saveToUndoStack();
    const newQuestion = buildNewQuestion(type, template.questions.length);
    setTemplate(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  // Frage an einer bestimmten Position (nach index) einfügen
  const handleAddQuestionAt = (index: number, type: Question['type'] = 'text') => {
    saveToUndoStack();
    setTemplate(prev => {
      const sorted = [...prev.questions].sort((a, b) => a.order - b.order);
      const newQuestion = buildNewQuestion(type);
      sorted.splice(index + 1, 0, newQuestion);
      return { ...prev, questions: sorted.map((q, i) => ({ ...q, order: i })) };
    });
  };

  // Reihenfolge per nativem Drag & Drop (Raster-fähig) ändern
  const handleDragReorder = (from: number, to: number) => {
    if (from === to) return;
    saveToUndoStack();
    setTemplate(prev => {
      const sorted = [...prev.questions].sort((a, b) => a.order - b.order);
      const [moved] = sorted.splice(from, 1);
      sorted.splice(to, 0, moved);
      return { ...prev, questions: sorted.map((q, i) => ({ ...q, order: i })) };
    });
  };

  // Alle Fragen auf-/zuklappen
  const allQuestionsExpanded =
    template.questions.length > 0 && expandedQuestions.size === template.questions.length;
  const toggleAllExpanded = () => {
    if (allQuestionsExpanded) {
      setExpandedQuestions(new Set());
    } else {
      setExpandedQuestions(new Set(template.questions.map(q => q.id)));
    }
  };
  const toggleQuestionExpanded = (questionId: string) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  const handleUpdateQuestion = (questionId: string, updatedQuestion: Question) => {
    saveToUndoStack();
    setTemplate(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? updatedQuestion : q
      )
    }));
  };

  const handleDeleteQuestion = (questionId: string) => {
    // **ENTFERNT: Browser-Popup durch direkte Löschung ersetzt**
    saveToUndoStack();
    setTemplate(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const handleMoveQuestion = (questionId: string, direction: 'up' | 'down') => {
    saveToUndoStack();
    const questions = [...template.questions];
    const currentIndex = questions.findIndex(q => q.id === questionId);
    
    if (direction === 'up' && currentIndex > 0) {
      [questions[currentIndex], questions[currentIndex - 1]] = 
      [questions[currentIndex - 1], questions[currentIndex]];
    } else if (direction === 'down' && currentIndex < questions.length - 1) {
      [questions[currentIndex], questions[currentIndex + 1]] = 
      [questions[currentIndex + 1], questions[currentIndex]];
    }
    
    questions.forEach((q, index) => {
      q.order = index;
    });
    
    setTemplate(prev => ({ ...prev, questions }));
  };

  const handleDuplicateQuestion = (questionId: string) => {
    saveToUndoStack();
    const originalQuestion = template.questions.find(q => q.id === questionId);
    if (originalQuestion) {
      const duplicatedQuestion: Question = {
        ...originalQuestion,
        id: `q_${Date.now()}`,
        order: template.questions.length
      };
      
      setTemplate(prev => ({
        ...prev,
        questions: [...prev.questions, duplicatedQuestion]
      }));
    }
  };

  // **FRAGETYPEN**
  const questionTypes = [
    { value: 'section', label: 'Überschrift', icon: '📚', description: 'Abschnitts-Überschrift' },
    { value: 'text', label: 'Text', icon: '📝', description: 'Einzeiliger Text' },
    { value: 'textarea', label: 'Textbereich', icon: '📄', description: 'Mehrzeiliger Text' },
    { value: 'radio', label: 'Auswahl', icon: '🔘', description: 'Eine Option wählen' },
    { value: 'multiselect', label: 'Mehrfach', icon: '☑️', description: 'Mehrere Optionen wählen' },
    { value: 'checkbox', label: 'Zustimmung (Checkbox)', icon: '✅', description: 'Zustimmung per Checkbox' },
    { value: 'select', label: 'Dropdown', icon: '📋', description: 'Eine Option aus Liste' },
    { value: 'file', label: 'Datei', icon: '📎', description: 'Datei hochladen' },
    { value: 'date', label: 'Datum', icon: '📅', description: 'Datumswahl' },
    { value: 'signature', label: 'Unterschrift', icon: '✍️', description: 'Digitale Unterschrift' },
    { value: 'info_text', label: 'Infotext', icon: 'ℹ️', description: 'Nur-Text für Informationen' },
    { value: 'admin_file', label: 'Admin-Datei', icon: '📁', description: 'Datei für Lieferanten bereitstellen' }
  ];

  // Gemeinsamer Sidebar-Inhalt für Desktop-Sidebar und Mobile-Overlay (kein Duplikat mehr)
  const renderSidebarContent = () => (
    <div className="space-y-5">
      {/* **ALLGEMEIN** */}
      <section>
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
          <FontAwesomeIcon icon={faInfoCircle} className="text-slate-400" />
          Allgemein
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Titel</label>
            <input
              type="text"
              value={template.title.de || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              className={`w-full px-3 py-2 bg-white border rounded-xl text-sm text-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 ${
                errors.titleDe ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              placeholder="Fragebogen Titel"
            />
            {errors.titleDe && <p className="text-red-500 text-xs mt-1">{errors.titleDe}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Beschreibung</label>
            <textarea
              value={template.description.de || ''}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 bg-white border rounded-xl text-sm text-slate-700 transition-colors resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 ${
                errors.descriptionDe ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              placeholder="Beschreibung"
            />
            {errors.descriptionDe && <p className="text-red-500 text-xs mt-1">{errors.descriptionDe}</p>}
          </div>
        </div>
      </section>

      {/* **EINSTELLUNGEN** */}
      <section>
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
          <FontAwesomeIcon icon={faCog} className="text-slate-400" />
          Einstellungen
        </h3>
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Fragebogen-Typ</label>
            <div className="relative">
              <select
                value={template.type}
                onChange={(e) => handleTemplateChange('type', e.target.value)}
                className="appearance-none w-full pl-3 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 cursor-pointer"
              >
                <option value="producer">🟢 Produzent</option>
                <option value="trader">🟠 Händler</option>
                <option value="trader_producer">🟣 Händler & Produzent</option>
                <option value="non_food">🔵 Non-Food</option>
                <option value="gmo_declaration">🧬 GVO-Erklärung</option>
                <option value="conformity_declaration">📋 Konformitätserklärung</option>
                <option value="emergency_contact">🚨 Notfallkontakt</option>
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Version</label>
            <input
              type="text"
              value={template.version}
              onChange={(e) => handleTemplateChange('version', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400"
              placeholder="1.0"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2">
            <div>
              <span className="text-sm font-medium text-slate-700">Status</span>
              <p className="text-xs text-slate-400">{template.isActive ? 'Aktiv' : 'Inaktiv'}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={template.isActive}
                onChange={(e) => handleTemplateChange('isActive', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[20px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:shadow after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2">
            <div>
              <span className="text-sm font-medium text-slate-700">Artikel abfragen</span>
              <p className="text-xs text-slate-400">Artikelbezug aktivieren</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={template.requiresArticles || false}
                onChange={(e) => handleTemplateChange('requiresArticles', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[20px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:shadow after:transition-all peer-checked:bg-red-500"></div>
            </label>
          </div>

          {template.requiresArticles && (
            <div>
              <button
                type="button"
                onClick={() => setShowArticleSelector(true)}
                className="w-full px-3 py-2 border border-dashed border-slate-300 rounded-xl text-sm text-slate-600 hover:border-red-400 hover:text-red-600 hover:bg-red-50/40 transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faBox} />
                {template.allArticlesSelected
                  ? '🌐 Alle Artikel'
                  : template.selectedArticles && template.selectedArticles.length > 0
                    ? `${template.selectedArticles.length} Artikel ausgewählt`
                    : 'Artikel auswählen'}
              </button>
              {template.allArticlesSelected && (
                <p className="text-xs text-emerald-600 mt-1.5 text-center">
                  ✅ Alle verfügbaren Artikel werden automatisch zugeordnet
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* **QUICK ADD SEKTION** */}
      <section>
        <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
          <FontAwesomeIcon icon={faPlus} className="text-slate-400" />
          Frage hinzufügen
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {questionTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => handleAddQuestion(type.value as Question['type'])}
              className="flex flex-col items-start gap-1.5 p-2.5 rounded-xl border border-slate-200 bg-white hover:border-red-300 hover:bg-red-50/40 hover:shadow-sm transition-all text-left group"
              title={type.description}
            >
              <span className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-white flex items-center justify-center text-base transition-colors">
                {type.icon}
              </span>
              <span className="text-xs font-medium text-slate-700 leading-tight">{type.label}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <Loader size="large" text="Fragebogen wird geladen..." />
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 flex flex-col">
      {/* **SUCCESS MESSAGE** */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="bg-emerald-50 border border-emerald-200 px-4 py-3 mx-4 mt-3 rounded-xl"
        >
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCheck} className="text-emerald-500 mr-3" />
            <p className="text-emerald-800 font-medium text-sm">{successMessage}</p>
          </div>
        </motion.div>
      )}

      {/* **HEADER** */}
      <header className="bg-white px-4 sm:px-6 py-3 flex items-center justify-between border-b border-slate-200/70 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/questionnaires')}
            className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
            title="Zurück"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate tracking-tight">
              {isEditing ? 'Fragebogen bearbeiten' : 'Neuer Fragebogen'}
            </h1>
            <p className="text-xs text-slate-400 truncate">
              {template.questions.length} Fragen · v{template.version}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Desktop controls */}
          <div className="hidden md:flex items-center gap-1 bg-slate-100 rounded-xl p-1">
            <button
              onClick={handleUndo}
              disabled={undoStack.length <= 1}
              className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              title="Rückgängig"
            >
              <FontAwesomeIcon icon={faUndo} size="sm" />
            </button>
            <button
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              title="Wiederholen"
            >
              <FontAwesomeIcon icon={faRedo} size="sm" />
            </button>
          </div>

          <button
            onClick={() => setShowPreview(true)}
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all"
          >
            <FontAwesomeIcon icon={faEye} className="text-xs" />
            Vorschau
          </button>

          {isEditing && (
            <button
              onClick={() => setShowVersionHistory(true)}
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-violet-600 bg-violet-50 border border-violet-100 rounded-xl hover:bg-violet-100 transition-all"
            >
              <FontAwesomeIcon icon={faHistory} className="text-xs" />
              Versionen
            </button>
          )}

          {/* Mobile controls */}
          <button
            onClick={() => setShowPreview(true)}
            className="md:hidden w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
            title="Vorschau"
          >
            <FontAwesomeIcon icon={faEye} size="sm" />
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white text-sm rounded-xl shadow-sm shadow-red-500/20 hover:brightness-105 disabled:opacity-50 transition-all font-semibold"
          >
            <FontAwesomeIcon icon={faSave} className="text-xs" />
            <span className="hidden sm:inline">{saving ? 'Speichert...' : 'Speichern'}</span>
            <span className="sm:hidden">{saving ? '...' : 'Save'}</span>
          </button>
        </div>
      </header>

      {/* **MAIN CONTENT** */}
      <div className="flex-1 flex overflow-hidden">
        {/* **LEFT SIDEBAR** */}
        <aside className="w-80 bg-white border-r border-slate-200/70 hidden lg:flex lg:flex-col">
          <div className="flex-1 overflow-y-auto">
            <div className="p-5">
              {renderSidebarContent()}
            </div>
          </div>
        </aside>

        {/* **MAIN QUESTION AREA** */}
        <main className="flex-1 min-w-0 flex flex-col">
          {/* Mobile Sidebar Toggle - nur auf kleinen Bildschirmen */}
          <div className="lg:hidden bg-white border-b border-slate-200/70 p-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">Fragebogen bearbeiten</h2>
              <button
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors"
              >
                <FontAwesomeIcon icon={faCog} />
                Einstellungen
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50">
            <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                  Fragen
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">{template.questions.length} insgesamt</p>
              </div>

              <div className="flex items-center gap-2">
                {template.questions.length > 0 && (
                  <button
                    onClick={toggleAllExpanded}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:border-slate-300 hover:text-slate-900 hover:shadow-sm transition-all"
                    title={allQuestionsExpanded ? 'Alle zuklappen' : 'Alle aufklappen'}
                  >
                    <FontAwesomeIcon icon={allQuestionsExpanded ? faAngleDoubleUp : faAngleDoubleDown} className="text-xs" />
                    <span className="hidden sm:inline">{allQuestionsExpanded ? 'Alle zuklappen' : 'Alle aufklappen'}</span>
                  </button>
                )}
                <button
                  onClick={() => handleAddQuestion('text')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:border-red-300 hover:text-red-600 hover:shadow-sm transition-all"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-xs" />
                  <span className="hidden sm:inline">Frage hinzufügen</span>
                  <span className="sm:hidden">Hinzufügen</span>
                </button>
              </div>
            </div>

            {errors.questions && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-center gap-2">
                <FontAwesomeIcon icon={faTimes} />
                {errors.questions}
              </div>
            )}

            <div className="space-y-3">
              {template.questions.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl bg-white">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center text-2xl">📝</div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">
                    Noch keine Fragen vorhanden
                  </h3>
                  <p className="text-xs text-slate-500 mb-5">
                    Fügen Sie Ihre erste Frage hinzu.
                  </p>
                  <button
                    onClick={() => handleAddQuestion('text')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-xl text-sm hover:brightness-105 transition-all font-semibold"
                  >
                    <FontAwesomeIcon icon={faPlus} className="text-xs" />
                    Erste Frage hinzufügen
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
                  {[...template.questions]
                    .sort((a, b) => a.order - b.order)
                    .map((question, index, arr) => (
                      <CompactQuestionEditor
                        key={question.id}
                        question={question}
                        index={index}
                        onUpdate={(updatedQuestion) => handleUpdateQuestion(question.id, updatedQuestion)}
                        onDelete={() => handleDeleteQuestion(question.id)}
                        onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                        onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                        onDuplicate={() => handleDuplicateQuestion(question.id)}
                        onAddBelow={() => handleAddQuestionAt(index, 'text')}
                        canMoveUp={index > 0}
                        canMoveDown={index < arr.length - 1}
                        errors={errors}
                        template={template}
                        isExpanded={expandedQuestions.has(question.id)}
                        onToggleExpanded={() => toggleQuestionExpanded(question.id)}
                        colSpanClass={
                          question.type === 'info_text' || expandedQuestions.has(question.id)
                            ? 'lg:col-span-2'
                            : ''
                        }
                        isDragging={dragIndex === index}
                        isDragOver={dragOverIndex === index && dragIndex !== null && dragIndex !== index}
                        onDragStartCard={() => setDragIndex(index)}
                        onDragEnterCard={() => setDragOverIndex(index)}
                        onDropCard={() => {
                          if (dragIndex !== null) handleDragReorder(dragIndex, index);
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                        onDragEndCard={() => {
                          setDragIndex(null);
                          setDragOverIndex(null);
                        }}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
          </div>
        </main>
        
        {/* Mobile Sidebar Overlay */}
        {showMobileSidebar && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowMobileSidebar(false)}></div>
            <div className="relative w-80 max-w-sm bg-white shadow-xl flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 border-b border-slate-200/70">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-bold text-slate-900">Einstellungen</h3>
                    <button
                      onClick={() => setShowMobileSidebar(false)}
                      className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
                
                <div className="p-5">
                  {renderSidebarContent()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* **MODALS** */}
      <AnimatePresence>
        {showPreview && (
          <PreviewModal
            template={template}
            language="de"
            onClose={() => setShowPreview(false)}
          />
        )}
        
        <AnimatePresence mode="wait">
          {showVersionHistory && id && (
            <VersionHistory
              templateId={id}
              templateTitle={template.title.de || template.title.en || 'Untitled'}
              currentVersion={template.version}
              onClose={() => setShowVersionHistory(false)}
              onVersionRestored={(newTemplate) => {
                setTemplate(newTemplate);
                setShowVersionHistory(false);
              }}
              onSelectVersion={(versionId: string) => {
                setShowVersionHistory(false);
                navigate(`/questionnaires/edit/${versionId}`);
              }}
            />
          )}
        </AnimatePresence>

        {/* **ARTIKEL SELECTOR MODAL** */}
        <ArticleSelector
          isOpen={showArticleSelector}
          onClose={() => setShowArticleSelector(false)}
          selectedArticles={template.selectedArticles || []}
          onSelectionChange={(articleIds) => {
            handleTemplateChange('selectedArticles', articleIds);
          }}
          title="Artikel für Fragebogen auswählen"
          allowSupplierSelection={true}
        />
      </AnimatePresence>
    </div>
  );
};

// **COMPACT QUESTION EDITOR KOMPONENTE**
interface CompactQuestionEditorProps {
  question: Question;
  index: number;
  onUpdate: (question: Question) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onAddBelow: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  errors: {[key: string]: string};
  template: QuestionnaireTemplate; // NEU: Template für Artikel-Zugriff
  isExpanded: boolean;
  onToggleExpanded: () => void;
  colSpanClass?: string;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStartCard: () => void;
  onDragEnterCard: () => void;
  onDropCard: () => void;
  onDragEndCard: () => void;
}

const CompactQuestionEditor: React.FC<CompactQuestionEditorProps> = ({
  question,
  index,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onAddBelow,
  canMoveUp,
  canMoveDown,
  errors,
  template, // NEU
  isExpanded,
  onToggleExpanded,
  colSpanClass = '',
  isDragging,
  isDragOver,
  onDragStartCard,
  onDragEnterCard,
  onDropCard,
  onDragEndCard
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showArticleSelector, setShowArticleSelector] = useState(false);
  const [currentOptionIndex, setCurrentOptionIndex] = useState<number | null>(null);
  const [isDraggable, setIsDraggable] = useState(false);

  // Titel-Textfeld automatisch an den Inhalt anpassen (mehrzeilig / Umbruch)
  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleFieldUpdate = (field: string, value: any) => {
    // Wenn requiresArticleSelection aktiviert wird, setze für alle Optionen die Template-Artikel
    if (field === 'requiresArticleSelection' && value === true) {
      // Prüfe ob alle Artikel ausgewählt sind oder spezifische IDs vorhanden sind
      const hasArticles = template.allArticlesSelected || 
                         (template.selectedArticles && template.selectedArticles.length > 0);
      
      const updatedOptions = (question.options || []).map(opt => ({
        ...opt,
        articleIds: hasArticles
          ? (template.selectedArticles && template.selectedArticles.length > 0 
              ? [...template.selectedArticles] 
              : []) // Bei allArticlesSelected bleibt leer, wird server-seitig aufgelöst
          : (opt.articleIds || [])
      }));
      
      onUpdate({
        ...question,
        [field]: value,
        options: updatedOptions
      });
    } else {
      onUpdate({
        ...question,
        [field]: value
      });
    }
  };

  const handleAdminFileUpload = async (files: FileList) => {
    if (!files || files.length === 0) return;

    try {
      setUploading(true);

      const result = await uploadAdminFiles(files);

      // API gibt result.data zurück, nicht result.success
      if (result.data && result.data.files && result.data.files.length > 0) {
        const newFiles = result.data.files.map((file: any) => ({
          fileName: file.fileName,
          originalName: file.originalName,
          size: file.size,
          uploadedAt: new Date().toISOString()
        }));

        const updatedFiles = [...(question.adminFiles || []), ...newFiles];

        // Direktes Update des Questions über onUpdate
        onUpdate({
          ...question,
          adminFiles: updatedFiles
        });
      } else {
        console.error('Upload failed - keine Dateien erhalten:', result);
        alert('Fehler: Keine Dateien wurden vom Server zurückgegeben');
      }
    } catch (error) {
      console.error('Error uploading admin files:', error);
      alert('Fehler beim Hochladen der Dateien');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAdminFile = (fileIndex: number) => {
    const updatedFiles = (question.adminFiles || []).filter((_, index) => index !== fileIndex);
    handleFieldUpdate('adminFiles', updatedFiles);
  };

  const handleLabelUpdate = (value: string) => {
    onUpdate({
      ...question,
      label: { de: value, en: value }
    });
  };

  const handleAddOption = () => {
    const newOption = {
      value: `option_${Date.now()}`,
      label: { de: '', en: '' },
      ...(question.type === 'multiselect' && { isCorrect: false }),
      // Wenn requiresArticleSelection aktiv ist, setze alle Template-Artikel als Standard
      ...(question.requiresArticleSelection && template.selectedArticles && template.selectedArticles.length > 0 && {
        articleIds: [...template.selectedArticles]
      })
    };
    
    onUpdate({
      ...question,
      options: [...(question.options || []), newOption]
    });
  };

  const handleAddOtherOption = () => {
    const otherOption = {
      value: `other_${Date.now()}`,
      label: { de: 'Anderes:', en: 'Other:' },
      isOther: true,
      ...(question.type === 'multiselect' && { isCorrect: false })
    };
    
    onUpdate({
      ...question,
      options: [...(question.options || []), otherOption]
    });
  };

  const handleUpdateOption = (optionIndex: number, field: string, value: any) => {
    const updatedOptions = [...(question.options || [])];
    
    if (field === 'label') {
      // Für "Anderes:"-Optionen den Text nicht ändern
      if (!updatedOptions[optionIndex].isOther) {
        updatedOptions[optionIndex].value = value;
        updatedOptions[optionIndex].label.de = value;
        updatedOptions[optionIndex].label.en = value;
      }
    } else if (field === 'isCorrect') {
      updatedOptions[optionIndex].isCorrect = value;
    }
    
    onUpdate({
      ...question,
      options: updatedOptions
    });
  };

  const handleRemoveOption = (optionIndex: number) => {
    const updatedOptions = question.options?.filter((_, index) => index !== optionIndex) || [];
    onUpdate({
      ...question,
      options: updatedOptions
    });
  };

  const getQuestionTypeIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      section: '📚', text: '📝', textarea: '📄', radio: '🔘', multiselect: '☑️', checkbox: '✅',
      select: '📋', file: '📎', date: '📅', signature: '✍️', info_text: 'ℹ️', admin_file: '📁'
    };
    return icons[type] || '❓';
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      section: 'Überschrift', text: 'Text', textarea: 'Textbereich', radio: 'Auswahl', multiselect: 'Mehrfach', checkbox: 'Zustimmung',
      select: 'Dropdown', file: 'Datei', date: 'Datum', signature: 'Unterschrift', info_text: 'Infotext', admin_file: 'Admin-Datei'
    };
    return labels[type] || type;
  };

  const needsOptions = ['radio', 'multiselect', 'select'].includes(question.type);
  const isCheckboxType = question.type === 'multiselect';
  const isSection = question.type === 'section';

  return (
    <div
      draggable={isDraggable}
      onDragStart={onDragStartCard}
      onDragEnter={onDragEnterCard}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => { setIsDraggable(false); onDropCard(); }}
      onDragEnd={() => { setIsDraggable(false); onDragEndCard(); }}
      className={`list-none transition-opacity ${colSpanClass} ${isDragging ? 'opacity-40' : ''}`}
    >
    <div className={`rounded-xl border shadow-sm hover:shadow-md transition-all duration-200 ${
      isDragOver ? 'ring-2 ring-red-300 border-red-300' : ''
    } ${
      question.type === 'section' 
        ? 'bg-blue-50/70 border-blue-200' 
        : 'bg-white border-slate-200/70 hover:border-slate-300'
    }`}>
      {/* **HEADER** */}
      <div className="p-3.5">
        <div className="flex items-center gap-2.5">
          {/* Drag-Handle */}
          <div
            onMouseDown={() => setIsDraggable(true)}
            onMouseUp={() => setIsDraggable(false)}
            className="flex-shrink-0 -ml-1 w-5 flex items-center justify-center text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none select-none"
            title="Zum Verschieben ziehen"
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </div>
          <div className="flex-shrink-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base ${
              question.type === 'section' 
                ? 'bg-blue-200/70 text-blue-700' 
                : 'bg-slate-100 text-slate-600'
            }`}>
              {getQuestionTypeIcon(question.type)}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-400">#{index + 1}</span>
              <span className="text-xs text-slate-400">{getQuestionTypeLabel(question.type)}</span>
              {question.required && (
                <span className="px-1.5 py-0.5 bg-red-50 text-red-600 text-xs font-medium rounded-md border border-red-100">
                  Pflicht
                </span>
              )}
            </div>
            {question.type === 'info_text' ? (
              <div className="mt-2">
                <RichTextEditor
                  value={question.label.de}
                  onChange={(value) => handleLabelUpdate(value)}
                  placeholder="Infotext eingeben... (nutzen Sie die Toolbar für Formatierungen wie **fett** oder Aufzählungen)"
                  className={errors[`question_${index}_label`] ? 'border-red-500' : ''}
                />
              </div>
            ) : (
              <textarea
                rows={1}
                ref={autoResize}
                value={question.label.de}
                onChange={(e) => { handleLabelUpdate(e.target.value); autoResize(e.target); }}
                className={`w-full text-sm font-medium bg-transparent border-0 border-b border-transparent hover:border-slate-200 focus:border-red-400 focus:outline-none transition-all py-1 mt-1 resize-none overflow-hidden break-words leading-snug ${
                  errors[`question_${index}_label`] 
                    ? 'text-red-600 border-red-500' 
                    : 'text-slate-900'
                }`}
                placeholder={question.type === 'section' ? 'Überschrift eingeben' : 'Frage eingeben'}
              />
            )}
            {errors[`question_${index}_label`] && (
              <span className="text-xs text-red-600 mt-1 block">
                {errors[`question_${index}_label`]}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-0.5 text-slate-400">
            <button
              onClick={onToggleExpanded}
              className="w-7 h-7 flex items-center justify-center hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
              title={isExpanded ? 'Einklappen' : 'Erweitern'}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size="sm" />
            </button>
            
            <button
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className="w-7 h-7 flex items-center justify-center hover:text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              title="Nach oben"
            >
              <FontAwesomeIcon icon={faArrowUp} size="sm" />
            </button>
            
            <button
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className="w-7 h-7 flex items-center justify-center hover:text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              title="Nach unten"
            >
              <FontAwesomeIcon icon={faArrowDown} size="sm" />
            </button>
            
            <button
              onClick={onDuplicate}
              className="w-7 h-7 flex items-center justify-center hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
              title="Duplizieren"
            >
              <FontAwesomeIcon icon={faCopy} size="sm" />
            </button>
            
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-7 h-7 flex items-center justify-center hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
              title="Löschen"
            >
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </button>
          </div>
        </div>
      </div>

      {/* **ERWEITERTE EXPANDED CONTENT** */}
      {isExpanded && (
        <div className="border-t border-slate-200/70 p-3.5 space-y-3 bg-slate-50/70">
          <div className="space-y-2.5">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Fragetyp
              </label>
              <div className="relative">
                <select
                  value={question.type}
                  onChange={(e) => handleFieldUpdate('type', e.target.value)}
                  className="appearance-none w-full pl-3 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 cursor-pointer"
                >
                  <option value="section">📚 Überschrift</option>
                  <option value="text">📝 Text</option>
                  <option value="textarea">📄 Textbereich</option>
                  <option value="radio">🔘 Auswahl (eine Option)</option>
                  <option value="multiselect">☑️ Mehrfach (mehrere Optionen)</option>
                  <option value="checkbox">✅ Zustimmung (Ja/Nein)</option>
                  <option value="select">📋 Dropdown (eine aus Liste)</option>
                  <option value="file">📎 Datei</option>
                  <option value="date">📅 Datum</option>
                  <option value="signature">✍️ Unterschrift</option>
                  <option value="info_text">ℹ️ Infotext</option>
                  <option value="admin_file">📁 Admin-Datei</option>
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {!['section', 'info_text', 'admin_file'].includes(question.type) && (
              <div className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2">
                <span className="text-sm font-medium text-slate-700">Erforderlich</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={question.required}
                    onChange={(e) => handleFieldUpdate('required', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[20px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:shadow after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>
            )}

            {/* **ARTIKEL HINZUFÜGEN CHECKBOX** */}
            {!['section', 'info_text', 'admin_file'].includes(question.type) && (
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2">
                  <span className="text-sm font-medium text-slate-700">Artikel hinzufügen</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={question.requiresArticleSelection || false}
                      onChange={(e) => handleFieldUpdate('requiresArticleSelection', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[20px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:shadow after:transition-all peer-checked:bg-red-500"></div>
                  </label>
                </div>
                
                {/* Hinweis wenn Template-Artikel vorhanden sind */}
                {question.requiresArticleSelection && (template.allArticlesSelected || (template.selectedArticles && template.selectedArticles.length > 0)) && (
                  <div className="ml-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                    ✅ {template.allArticlesSelected 
                      ? 'Alle Template-Artikel (32.744) werden automatisch hinzugefügt' 
                      : `${template.selectedArticles?.length || 0} Template-Artikel werden automatisch hinzugefügt`
                    }
                  </div>
                )}

                {question.requiresArticleSelection && (
                  <div className="ml-2">
                    <div className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2">
                      <span className="text-sm font-medium text-slate-700">Mehrere Artikel pro Antwort</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={question.allowMultipleArticles || false}
                          onChange={(e) => handleFieldUpdate('allowMultipleArticles', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[20px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:shadow after:transition-all peer-checked:bg-red-500"></div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* **SIGNATURE FELDER** */}
          {question.type === 'signature' && (
            <div className="border border-blue-200 rounded p-2 bg-blue-50">
              <h4 className="text-xs font-medium text-blue-800 mb-2">
                ✍️ Unterschrift-Einstellungen
              </h4>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Unterschrift-Text
                  </label>
                  <input
                    type="text"
                    value={question.signatureText || 'Ich bestätige hiermit...'}
                    onChange={(e) => handleFieldUpdate('signatureText', e.target.value)}
                    onFocus={() => {
                      // Wenn signatureText leer ist, setze den Default-Wert
                      if (!question.signatureText) {
                        handleFieldUpdate('signatureText', 'Ich bestätige hiermit...');
                      }
                    }}
                    className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      errors[`question_${index}_signatureText`] 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Text für Unterschrift"
                  />
                  {errors[`question_${index}_signatureText`] && (
                    <span className="text-xs text-red-600 mt-1 block">
                      {errors[`question_${index}_signatureText`]}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={question.requireDate || false}
                      onChange={(e) => handleFieldUpdate('requireDate', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-2 text-xs font-medium text-gray-700">Datum erforderlich</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* **INFO TEXT FELDER** */}
          {question.type === 'info_text' && (
            <div className="border border-blue-200 rounded p-2 bg-blue-50">
              <h4 className="text-xs font-medium text-blue-800 mb-2">ℹ️ Infotext-Einstellungen</h4>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Informationstext (Deutsch)</label>
                  <textarea
                    value={question.infoText?.de || ''}
                    onChange={(e) => handleFieldUpdate('infoText', { 
                      ...question.infoText, 
                      de: e.target.value 
                    })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y min-h-[120px]"
                    placeholder="Informationstext auf Deutsch... (mehrzeilig möglich, z.B. mit Aufzählungen: -1 -2 -3)"
                    rows={8}
                    style={{ fontFamily: 'monospace, Consolas, "Courier New", courier' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Informationstext (Englisch)</label>
                  <textarea
                    value={question.infoText?.en || ''}
                    onChange={(e) => handleFieldUpdate('infoText', { 
                      ...question.infoText, 
                      en: e.target.value 
                    })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y min-h-[120px]"
                    placeholder="Information text in English... (multiple lines possible, e.g. with bullet points: -1 -2 -3)"
                    rows={8}
                    style={{ fontFamily: 'monospace, Consolas, "Courier New", courier' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* **ADMIN FILE FELDER** */}
          {question.type === 'admin_file' && (
            <div className="border border-green-200 rounded p-2 bg-green-50">
              <h4 className="text-xs font-medium text-green-800 mb-2">📁 Admin-Datei-Einstellungen</h4>
              <div className="space-y-3">
                {/* File Upload */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Dateien für Lieferanten hochladen
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          handleAdminFileUpload(e.target.files);
                        }
                      }}
                      className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      disabled={uploading}
                    />
                    {uploading && (
                      <div className="text-xs text-blue-600">
                        <FontAwesomeIcon icon={faUpload} className="animate-spin mr-1" />
                        Lädt hoch...
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Mehrere Dateien auswählbar. Max. 10MB pro Datei.
                  </p>
                </div>

                {/* File List */}
                {question.adminFiles && question.adminFiles.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Hochgeladene Dateien ({question.adminFiles.length})
                    </label>
                    <div className="space-y-2">
                      {question.adminFiles.map((file: any, fileIndex: number) => (
                        <div key={fileIndex} className="flex items-center justify-between p-2 bg-white rounded border border-green-300">
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faDownload} className="text-green-600 text-xs" />
                            <div>
                              <div className="text-xs font-medium text-gray-900">{file.originalName}</div>
                              <div className="text-xs text-gray-500">
                                {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unbekannte Größe'} • 
                                {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString('de-DE') : 'Heute'}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveAdminFile(fileIndex)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
                            title="Datei entfernen"
                          >
                            <FontAwesomeIcon icon={faTrash} size="xs" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!question.adminFiles || question.adminFiles.length === 0) && (
                  <div className="text-center py-3 text-gray-500 border-2 border-dashed border-gray-300 rounded">
                    <FontAwesomeIcon icon={faUpload} className="text-gray-400 mb-1" />
                    <p className="text-xs">Keine Dateien hochgeladen</p>
                    <p className="text-xs">Wählen Sie Dateien aus, die Lieferanten herunterladen können</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* **SECTION INFO** */}
          {question.type === 'section' && (
            <div className="border border-blue-200 rounded p-2 bg-blue-50">
              <div className="flex items-center text-xs text-blue-700">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                <span>Überschriften dienen zur Strukturierung des Fragebogens und benötigen keine Antworten.</span>
              </div>
            </div>
          )}

          {/* **OPTIONS MIT RUNDEN BUTTONS** */}
          {needsOptions && (
            <div className="border border-gray-200 rounded p-2 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-gray-700">
                  Antwortoptionen
                  {isCheckboxType && <span className="text-blue-600 ml-1">(mit Bewertung)</span>}
                </h4>
                <div className="flex space-x-1">
                  <button
                    onClick={handleAddOption}
                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-all"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Option
                  </button>
                  <button
                    onClick={handleAddOtherOption}
                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-all"
                    title="Hinzufügen einer 'Anderes:'-Option"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Anderes
                  </button>
                </div>
              </div>
              
              {(question.options || []).length > 0 ? (
                <div className="space-y-1.5">
                  {(question.options || []).map((option, optionIndex) => (
                    <div key={optionIndex} className={`p-2 rounded border ${option.isOther ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center space-x-2">
                        <div className={`w-5 h-5 rounded flex items-center justify-center text-xs font-medium border ${
                          option.isOther 
                            ? 'bg-green-100 text-green-700 border-green-300' 
                            : 'bg-white text-gray-600 border-gray-300'
                        }`}>
                          {option.isOther ? '?' : optionIndex + 1}
                        </div>
                        <input
                          type="text"
                          value={option.label.de}
                          onChange={(e) => handleUpdateOption(optionIndex, 'label', e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder={option.isOther ? "Beschriftung für 'Anderes'" : "Beschriftung"}
                          readOnly={option.isOther}
                        />
                        
                        {/* **RUNDE RICHTIG/FALSCH BUTTONS** */}
                        {isCheckboxType && (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleUpdateOption(optionIndex, 'isCorrect', true)}
                              className={`w-6 h-6 rounded-full text-xs transition-all flex items-center justify-center ${
                                option.isCorrect === true
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-200 text-gray-500 hover:bg-green-100'
                              }`}
                              title="Richtige Antwort"
                            >
                              <FontAwesomeIcon icon={faCheck} size="xs" />
                            </button>
                            <button
                              onClick={() => handleUpdateOption(optionIndex, 'isCorrect', false)}
                              className={`w-6 h-6 rounded-full text-xs transition-all flex items-center justify-center ${
                                option.isCorrect === false
                                  ? 'bg-red-500 text-white'
                                  : 'bg-gray-200 text-gray-500 hover:bg-red-100'
                              }`}
                              title="Falsche Antwort"
                            >
                              <FontAwesomeIcon icon={faX} size="xs" />
                            </button>
                          </div>
                        )}
                        
                        {/* **ARTIKEL AUSWÄHLEN BUTTON** */}
                        {question.requiresArticleSelection && (
                          <button
                            onClick={() => {
                              setCurrentOptionIndex(optionIndex);
                              setShowArticleSelector(true);
                            }}
                            className={`px-2 py-1 text-xs rounded hover:bg-blue-200 transition-all flex items-center ${
                              option.articleIds && option.articleIds.length > 0 
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-blue-100 text-blue-700'
                            }`}
                            title="Artikel für diese Option auswählen"
                          >
                            <FontAwesomeIcon icon={faBox} className="mr-1" size="xs" />
                            {option.articleIds && option.articleIds.length > 0 
                              ? (template.allArticlesSelected 
                                  ? '✓ Alle Artikel' 
                                  : `✓ ${option.articleIds.length} Artikel`)
                              : 'Artikel'
                            }
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleRemoveOption(optionIndex)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
                        >
                          <FontAwesomeIcon icon={faTrash} size="xs" />
                        </button>
                      </div>
                      
                      {/* **KOMPAKTE STATUS ANZEIGE** */}
                      {isCheckboxType && (
                        <div className="mt-1">
                          {option.isCorrect === true && (
                            <span className="inline-flex items-center text-xs text-green-600">
                              <FontAwesomeIcon icon={faCheck} className="mr-1" size="xs" />
                              Richtig
                            </span>
                          )}
                          {option.isCorrect === false && (
                            <span className="inline-flex items-center text-xs text-red-600">
                              <FontAwesomeIcon icon={faX} className="mr-1" size="xs" />
                              Falsch
                            </span>
                          )}
                          {option.isCorrect === undefined && (
                            <span className="text-xs text-gray-500">Nicht bewertet</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-2 text-gray-500">
                  <p className="text-xs">Keine Optionen vorhanden</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* **DELETE CONFIRMATION MODAL** */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Frage löschen</h3>
                  <p className="text-sm text-gray-500">Diese Aktion kann nicht rückgängig gemacht werden.</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded p-3 mb-4">
                <p className="text-sm text-gray-700 font-medium">
                  #{index + 1}: {question.label.de || 'Unbenannte Frage'}
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    onDelete();
                    setShowDeleteConfirm(false);
                  }}
                  className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Löschen
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* **ARTIKEL SELECTOR FÜR OPTIONEN** */}
      <ArticleSelector
        isOpen={showArticleSelector && currentOptionIndex !== null}
        onClose={() => {
          setShowArticleSelector(false);
          setCurrentOptionIndex(null);
        }}
        selectedArticles={
          currentOptionIndex !== null && question.options?.[currentOptionIndex]?.articleIds || []
        }
        onSelectionChange={(articleIds) => {
          if (currentOptionIndex !== null) {
            const updatedOptions = [...(question.options || [])];
            updatedOptions[currentOptionIndex] = {
              ...updatedOptions[currentOptionIndex],
              articleIds
            };
            onUpdate({
              ...question,
              options: updatedOptions
            });
          }
        }}
        title={`Artikel für Option ${currentOptionIndex !== null ? currentOptionIndex + 1 : ''} auswählen`}
        allowSupplierSelection={true}
      />
    </div>

    {/* Frage darunter einfügen */}
    <div className="group/add relative flex items-center justify-center h-2 hover:h-9 transition-all duration-150">
      <button
        onClick={onAddBelow}
        className="opacity-0 group-hover/add:opacity-100 inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-dashed border-slate-300 text-slate-500 rounded-full text-xs font-medium hover:border-red-400 hover:text-red-600 hover:bg-red-50/40 shadow-sm transition-all"
        title="Frage hier einfügen"
      >
        <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
        Frage einfügen
      </button>
    </div>
    </div>
  );
};

export default QuestionnaireEditor;